import type { BeforeSendFn, CaptureResult } from 'posthog-js';

/**
 * The PostHog browser key falls back to a baked-in value in `src/config.ts`, so a dev machine
 * starts reporting into the staging project the moment the app boots. Sentry stays dark locally
 * because its DSN defaults to empty; PostHog has no such guard, and dev traffic had grown to the
 * second-heaviest host in the project.
 *
 * Deployed hosts deliberately are NOT enumerated. Customer custom domains are open-ended, and a
 * new one silently losing telemetry is far worse than a preview environment reporting noise, so
 * this denies the hosts known not to be real traffic and lets everything else through.
 */
const NON_DEPLOYED_HOSTNAMES = new Set([
  'localhost',
  '127.0.0.1',
  '::1',
  '[::1]',
]);

// `.localhost` covers the subdomain dev hosts (studio.localhost); `.up.railway.app` covers the
// per-PR preview deploys.
const NON_DEPLOYED_HOSTNAME_SUFFIXES = ['.localhost', '.up.railway.app'];

/** Set this to "true" in devtools to report from a dev machine while testing instrumentation. */
export const LOCAL_CAPTURE_OVERRIDE_KEY = 'posthog_capture_on_local';

export const isNonDeployedHost = (hostname: string): boolean => {
  const normalized = hostname.trim().toLowerCase();
  if (NON_DEPLOYED_HOSTNAMES.has(normalized)) {
    return true;
  }
  return NON_DEPLOYED_HOSTNAME_SUFFIXES.some((suffix) =>
    normalized.endsWith(suffix),
  );
};

// Storage access throws outright in some privacy modes, so a broken override must not take the
// capture decision down with it.
const hasLocalCaptureOverride = (storage?: Storage): boolean => {
  try {
    return storage?.getItem(LOCAL_CAPTURE_OVERRIDE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const shouldCaptureFromHost = (
  hostname: string,
  storage?: Storage,
): boolean => !isNonDeployedHost(hostname) || hasLocalCaptureOverride(storage);

/**
 * Returns a `before_send` that drops every event, or undefined on a deployed host so the capture
 * path keeps its normal zero-overhead shape. Evaluated once at init: flipping the override takes
 * effect on reload, which is enough for a debugging aid.
 */
export const buildHostGate = (
  hostname: string,
  storage?: Storage,
): BeforeSendFn | undefined => {
  if (shouldCaptureFromHost(hostname, storage)) {
    return undefined;
  }
  return (_event: CaptureResult | null): CaptureResult | null => null;
};

// Returns null rather than undefined so callers can pass an explicit "no window" that a default
// parameter would otherwise overwrite.
const ambientWindow = (): Window | null =>
  typeof window === 'undefined' ? null : window;

/**
 * The browser-side entry point. `App` is also rendered through `ReactDOM.renderToString`, where
 * there is no `window` at all, so resolving the globals has to live behind this guard rather than
 * at the call site. No gate is needed on the server: posthog-js only ever captures in the browser,
 * and hydration re-evaluates this with a real window.
 */
export const buildBrowserHostGate = (
  win: Window | null = ambientWindow(),
): BeforeSendFn | undefined => {
  if (!win) {
    return undefined;
  }
  // Reading the property itself throws when site data is blocked, not just reading a key from it.
  let storage: Storage | undefined;
  try {
    storage = win.localStorage;
  } catch {
    storage = undefined;
  }
  return buildHostGate(win.location.hostname, storage);
};
